import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { NoticesAPI } from "@/lib/sprint7-api";
import { TenantsAPI } from "@/lib/sprint2-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Megaphone, Plus, Loader2, Trash2, Paperclip, FileText, Video } from "lucide-react";

const COMPOSE_ROLES = new Set(["super_admin", "inst_admin", "principal", "hod", "teacher"]);

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function useTenantId() {
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("super_admin");
  const tQ = useQuery({
    queryKey: ["notices-tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });
  if (!isSuper) return user?.tenant_id || null;
  return tQ.data?.[0]?.id || null;
}

function AttachmentTile({ a }) {
  const url = NoticesAPI.attachmentUrl(a.id);
  const ct = a.content_type || "";
  if (ct.startsWith("image/")) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block rounded-md overflow-hidden border border-border bg-muted/30 hover:border-primary/40 transition" data-testid={`notice-img-${a.id}`}>
        <img src={url} alt={a.filename} className="max-h-[280px] w-auto object-contain" loading="lazy" />
      </a>
    );
  }
  if (ct.startsWith("video/")) {
    return (
      <video controls className="rounded-md max-h-[280px] border border-border" data-testid={`notice-vid-${a.id}`}>
        <source src={url} type={ct} />
      </video>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-border rounded-md bg-muted/30 hover:bg-muted transition" data-testid={`notice-doc-${a.id}`}>
      {ct.startsWith("video/") ? <Video className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
      <span>{a.filename}</span>
    </a>
  );
}

export default function Notices() {
  const { user } = useAuth();
  const tenantId = useTenantId();
  const canCompose = (user?.roles || []).some((r) => COMPOSE_ROLES.has(r));
  const canDelete = (user?.roles || []).some((r) => ["super_admin", "inst_admin", "principal"].includes(r));
  const qc = useQueryClient();

  const [sortDir, setSortDir] = useState("new");
  const [typeFilter, setTypeFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");

  const boardQ = useQuery({
    queryKey: ["notices-board", tenantId],
    queryFn: () => NoticesAPI.board(tenantId ? { tenant_id: tenantId, limit: 200 } : { limit: 200 }),
    enabled: !!tenantId,
  });

  const onDelete = async (id) => {
    try {
      await NoticesAPI.remove(id);
      toast.success("Notice removed from the Notice Board");
      qc.invalidateQueries({ queryKey: ["notices-board"] });
      qc.invalidateQueries({ queryKey: ["notices-dash"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  };

  const rows = useMemo(() => {
    let arr = [...(boardQ.data || [])];
    if (typeFilter !== "all") arr = arr.filter((n) => (n.notice_type || "general") === typeFilter);
    if (yearFilter !== "all") arr = arr.filter((n) => new Date(n.published_at).getFullYear() === Number(yearFilter));
    if (monthFilter !== "all") arr = arr.filter((n) => new Date(n.published_at).getMonth() === Number(monthFilter));
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase();
      arr = arr.filter((n) =>
        (n.title || "").toLowerCase().includes(q) ||
        (n.body || "").toLowerCase().includes(q) ||
        (n.audience_label || "").toLowerCase().includes(q)
      );
    }
    arr.sort((a, b) => {
      const ta = new Date(a.published_at).getTime();
      const tb = new Date(b.published_at).getTime();
      return sortDir === "new" ? tb - ta : ta - tb;
    });
    return arr;
  }, [boardQ.data, typeFilter, yearFilter, monthFilter, searchQ, sortDir]);

  const years = useMemo(() => {
    const set = new Set((boardQ.data || []).map((n) => new Date(n.published_at).getFullYear()));
    return Array.from(set).sort((a, b) => b - a);
  }, [boardQ.data]);

  return (
    <AppShell>
      <div className="p-8" data-testid="notices-page">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="font-display font-extrabold tracking-tight text-4xl">Notice Board</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              The digital notice board — every notice published in this tenant is listed here in
              chronological order. Admins can delete a notice; it disappears from every recipient&apos;s
              board (web + mobile app).
            </p>
          </div>
          {canCompose && (
            <Button asChild data-testid="create-notice-btn">
              <Link to="/notices/create">
                <Plus className="h-4 w-4 mr-1.5" />
                Create Notice
              </Link>
            </Button>
          )}
        </div>

        {/* Sort & filter row */}
        <div className="bg-muted/40 rounded-xl p-3 mb-3">
          <div className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground mb-2">Sort Notice by</div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={sortDir} onValueChange={setSortDir}>
              <SelectTrigger className="h-8 w-[140px]" data-testid="sort-dir"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New to Old</SelectItem>
                <SelectItem value="old">Old to New</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-8 w-[140px]" data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="exam">Exam</SelectItem>
                <SelectItem value="holiday">Holiday</SelectItem>
                <SelectItem value="event">Event</SelectItem>
                <SelectItem value="fees">Fees</SelectItem>
                <SelectItem value="circular">Circular</SelectItem>
                <SelectItem value="emergency">Emergency</SelectItem>
              </SelectContent>
            </Select>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="h-8 w-[120px]" data-testid="filter-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={monthFilter} onValueChange={setMonthFilter}>
              <SelectTrigger className="h-8 w-[120px]" data-testid="filter-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
                  <SelectItem key={m} value={String(i)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Search notices…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              className="h-8 w-[220px]"
              data-testid="filter-search"
            />
          </div>
        </div>

        <div className="text-sm text-muted-foreground mb-3" data-testid="board-summary">
          {boardQ.isLoading ? "Loading…" : `${rows.length} notice${rows.length === 1 ? "" : "s"} on the board`}
        </div>

        {boardQ.isLoading ? (
          <div className="bg-card rounded-2xl shadow-sm p-8 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div
            className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground"
            data-testid="board-empty"
          >
            <Megaphone className="h-10 w-10 mx-auto opacity-40 mb-3" />
            <div className="text-sm">No notices match your filters.</div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="board-list">
            {rows.map((n) => {
              const imgAttachments = (n.attachments || []).filter((a) => (a.content_type || "").startsWith("image/"));
              const otherAttachments = (n.attachments || []).filter((a) => !(a.content_type || "").startsWith("image/"));
              return (
                <article
                  key={n.id}
                  className="bg-card rounded-2xl shadow-sm p-5 hover:shadow-md transition-shadow"
                  data-testid={`notice-row-${n.id}`}
                >
                  <header className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="outline" className="text-[10px] capitalize">{n.notice_type || "general"}</Badge>
                        <h3 className="font-semibold tracking-tight text-base">{n.title}</h3>
                      </div>
                      <div className="text-xs text-primary mt-0.5 font-medium" data-testid={`notice-audience-${n.id}`}>
                        {n.audience_label}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {n.author_email || "—"} · {fmtTime(n.published_at)} · {n.recipients} recipient{n.recipients === 1 ? "" : "s"} · {n.read_count} read
                      </div>
                    </div>
                    {canDelete && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" data-testid={`delete-${n.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this notice?</AlertDialogTitle>
                            <AlertDialogDescription>
                              The notice &ldquo;{n.title}&rdquo; will be removed from the Notice Board for every recipient (web + mobile).
                              This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => onDelete(n.id)} className="bg-destructive hover:bg-destructive/90" data-testid={`confirm-delete-${n.id}`}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </header>

                  {/* Body — HTML from the rich-text editor, sanitised via DOMPurify
                      to strip <script>, event handlers, javascript: URIs, etc. */}
                  {n.body && (
                    <div
                      className="prose prose-sm max-w-none text-sm leading-relaxed mb-3 [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(n.body, {
                          ALLOWED_TAGS: [
                            "p", "br", "strong", "em", "u", "s", "a",
                            "ul", "ol", "li", "blockquote",
                            "h1", "h2", "h3", "h4", "h5", "h6",
                            "img", "code", "pre", "hr", "span", "div",
                          ],
                          ALLOWED_ATTR: ["href", "src", "alt", "title", "target", "rel", "class"],
                          ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
                        }),
                      }}
                    />
                  )}

                  {/* Inline image previews */}
                  {imgAttachments.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-2">
                      {imgAttachments.map((a) => <AttachmentTile key={a.id} a={a} />)}
                    </div>
                  )}

                  {/* Doc / video chips */}
                  {otherAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {otherAttachments.map((a) => <AttachmentTile key={a.id} a={a} />)}
                    </div>
                  )}

                  {(n.attachments || []).length > 0 && (
                    <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-2">
                      <Paperclip className="h-3 w-3" /> {n.attachments.length} attachment{n.attachments.length === 1 ? "" : "s"}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
